import {Action, QuerySegmentEntry, SelectorTypeDefinitions} from 'common/query-builder/query-entry-section/types';
import {FullPropertyType, PropertyType} from 'common/report/constants';
import {
  getSegmentValuePropertyType,
  getSegmentValueResourceType,
  isCohortSegment,
  isListOfObjectsSegment,
  isSegmentation,
  SegmentValue,
} from 'common/query-builder/query-block/segment-menu/types';
import {getSegmentTrackingProps} from 'common/query-builder/query-block/segment-menu/util';
import * as PropertiesMenu from 'common/query-builder/query-block/properties-menu/types';
import {PropertySectionItem} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {ActionType, trackLearningAction} from 'common/util/server-tracking';
import {Segmentation} from 'common/types/reports/query';
import {getIconForPropertyType} from 'common/report/util/properties';

import {BaseEntryRow} from '../base';
import type {EntryRow} from '../types';
import {getPropertyType, setPropertyType} from '../row-util';
import {
  getIconForBehavior,
  isEventBasedBehavioralSegmentation,
  isMultiAttributionSegmentation,
} from '../behavior/utils';
import {SegmentEntryRowEl} from './template';

export class SegmentEntryRow extends BaseEntryRow<QuerySegmentEntry> implements EntryRow<QuerySegmentEntry> {
  template = SegmentEntryRowEl;

  isPlaceholder() {
    if (
      isEventBasedBehavioralSegmentation(this.entry.event) &&
      (this.entry.event.behavior?.property?.value === null ||
        this.entry.event.behavior?.event?.value === null ||
        this.entry.event.behavior?.aggregationOperator === null)
    ) {
      return true;
    }

    const segmentation = this.entry.event as Segmentation;

    if (isListOfObjectsSegment(segmentation) && Boolean(segmentation?.listItemGroup?.propertyName) === false) {
      return true;
    }

    return Boolean(this.entry.event) === false;
  }

  isIntermediatePlaceholder() {
    return this.isPlaceholder() && !!this.entry.event;
  }

  selectorIcon(): string {
    if (!this.entry.event) {
      return `search`;
    }

    const segment = this.entry.event as Segmentation;
    if (isCohortSegment(segment)) {
      return `community`;
    }
    if (isEventBasedBehavioralSegmentation(segment) || isMultiAttributionSegmentation(segment)) {
      return getIconForBehavior(segment.behavior);
    }
    return getIconForPropertyType(getSegmentValuePropertyType(segment), {
      isCustom: segment && `customProperty` in segment,
    });
  }

  /**
   * Generates a new empty QueryFilterEntry
   */
  static getNewEntry(): QuerySegmentEntry {
    return {
      type: SelectorTypeDefinitions.Segment,
      filtersOperator: null,
      filters: [],
      event: null,
    };
  }

  getPropertyType(): FullPropertyType {
    if (Array.isArray(this.entry.event)) {
      return {
        type: PropertyType.String, // default subtype for lists when we don't know otherwise
        isList: true,
      };
    }

    return getPropertyType(this.entry.event);
  }

  setPropertyType(toType: FullPropertyType) {
    let segment = this.entry.event;

    if (!isCohortSegment(segment)) {
      if (Array.isArray(segment)) {
        segment = segment[0];
      }

      const newSegment = setPropertyType(segment, toType);

      this.section.updateEntries(Action.Edit, {entry: {...this.entry, event: newSegment}, index: this.index});
    } else {
      throw Error(`typecast operation not possible`);
    }
  }

  handleListObjectPropertySelect = (selection: Segmentation) => {
    const segment = this.entry.event as Segmentation;
    if (selection.unit) {
      // for date properties, "unit" should be outside of listItemGroup (just like the other custom segment
      // properties `customGroups` and `customBucket`)
      segment.unit = selection.unit;
      delete selection.unit;
    }
    segment.listItemGroup = selection;
    this.triggerUpdate(Action.Edit, segment, QueryReason.QbBreakdownUpdate);
  };

  triggerUpdate(action: Action, value: SegmentValue, queryReason: QueryReason, rawItem = null) {
    queryReason = queryReason ?? QueryReason.QbBreakdownUpdate;
    const source = getSegmentValueResourceType(value);
    const trackingProps = this.trackingProps(value, rawItem);

    if (
      queryReason === QueryReason.QbBreakdownUpdate &&
      this.section.attr(`property-combine-mode`) !== PropertiesMenu.PropertyCombineMode.Intersection
    ) {
      // for cases where the segmentation menu is used outside of breakdowns, disable
      // learning action tracking
      trackLearningAction({
        projectId: this.section.attr(`project-id`),
        reportName: this.section.attr(`track-name`),
        actionType: ActionType.Breakdown,
        resourceType: source,
      });
    }

    // when de-selecting all selected items, remove the current entry
    if (Array.isArray(value) && value.length === 0) {
      action = Action.Remove;
      queryReason = QueryReason.QbOtherUpdate;
    }

    // If we change the property for multi attribution, we need to clear the filters
    if (
      isSegmentation(value) &&
      isSegmentation(this.entry.event) &&
      isMultiAttributionSegmentation(value) &&
      value.propertyName !== this.entry.event.propertyName
    ) {
      value.behavior.filters = [];
    }

    this.section.updateEntries(action, {
      index: this.index,
      entry: {...this.entry, event: value},
      trackingProps,
      queryReason,
    });
  }

  /**
   * Generates tracking props for the selected segment
   */
  private trackingProps(selection: SegmentValue, rawItem?: PropertySectionItem) {
    let extraProps = {};

    if (isCohortSegment(selection) && rawItem) {
      extraProps = {
        'cohort type': rawItem.createdBy
          ? rawItem.createdBy.id === this.section.attr(`user-id`)
            ? `cohorts-yours`
            : `cohorts`
          : `inline cohorts`,
      };
    }
    return {...extraProps, ...getSegmentTrackingProps(selection)};
  }
}
